// src/lib/brand.ts
// First-party marketing domains. seaaii.com is a second public front-end for
// the *same* FlowCloser app — it serves its own landing page at "/", but the
// dashboard, auth, and API underneath are shared. This is distinct from a
// customer's custom sale-page domain (handled in middleware via /s/custom).
export const SEAAII_HOSTS = new Set([
  'seaaii.com',
  'www.seaaii.com',
  'seaaii.flowcloser.com',
  // Staging box — same Seaaii front-end (landing + dashboard + partner) for
  // pre-prod testing. Must be recognised here or middleware would treat it as
  // a customer custom sale-page domain and 404.
  'staging.seaaii.com',
  'www.staging.seaaii.com',
  // Local dev only: *.localhost auto-resolves to 127.0.0.1 in every browser,
  // so `http://seaaii.localhost:3000/auth/signin` renders the Seaaii front-end
  // with no /etc/hosts edit or Chrome flags. Can never be a real production
  // host, so it's safe to keep in the set.
  'seaaii.localhost',
])

// True when the request host is the Seaaii marketing domain. Used to (a) serve
// the Seaaii landing in middleware and (b) hide Google/LINE login on the shared
// auth pages — those OAuth callbacks are registered to flowcloser.com only, so
// social login can't complete on seaaii.com anyway.
export function isSeaaiiHost(host?: string | null): boolean {
  if (!host) return false
  return SEAAII_HOSTS.has(host.split(':')[0].toLowerCase())
}

// The Partner that owns every self-signup coming from the Seaaii front-end.
// These signups are treated as leads: created with PENDING approval under
// this partner, who reviews + approves them from /partner/members before the
// customer can log in. Hardcoded by request (single, stable partner).
export const SEAAII_PARTNER_ID = 'df0a4e84-7c9a-4f09-9dbd-10e68311df17'

// Seaaii team inbox — demo-booking confirmations are sent here alongside the
// customer so the sale team sees every new appointment.
export const SEAAII_SUCCESS_EMAIL = 'success@seaaii.com'

// โดเมน FlowCloser เดิม — ยังเป็น first-party ตลอดช่วงเปลี่ยนผ่านไป seaaii.com
// (webhook/ลิงก์เก่าชี้อยู่) middleware ใช้ตัดสิน custom-domain ด้วย ห้ามผูก
// กับ NEXT_PUBLIC_BASE_URL ไม่งั้นสลับ env แล้วโดเมนเก่ากลายเป็น sale page 404
export const FLOWCLOSER_HOSTS = new Set(['flowcloser.com', 'www.flowcloser.com'])

// คืน origin (https://<host>) เมื่อ host เป็นโดเมน first-party ของเรา — ใช้สร้าง
// OAuth redirect_uri ตามโดเมนที่ผู้ใช้เปิดอยู่จริง ให้ flow เชื่อมเพจ FB จบบน
// แบรนด์เดียวกัน (กดจาก seaaii.com ไม่เด้งไปจบที่ flowcloser.com — คนละ cookie
// จะกลายเป็นหลุด login). URI ของทุก host ในนี้ต้องอยู่ใน Valid OAuth Redirect
// URIs ของแอพ FB ด้วย. host แปลกหน้า (custom domain ลูกค้า / localhost dev)
// → null ให้ caller fallback NEXT_PUBLIC_BASE_URL ตามเดิม
export function firstPartyOrigin(host?: string | null): string | null {
  if (!host) return null
  const h = host.split(':')[0].toLowerCase()
  if (h === 'localhost' || h.endsWith('.localhost')) return null
  if (FLOWCLOSER_HOSTS.has(h) || SEAAII_HOSTS.has(h)) return `https://${h}`
  return null
}
